import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, Optional, OnDestroy } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { Viewport } from '@egovsolutions/angular-viewport';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, skip, skipWhile, switchMap, take } from 'rxjs/operators';
import { ScrollableModal } from '@egovsolutions/angular-scrollable-modal';
import { DialogNavigations } from '@egovsolutions/angular-dialog-navigations';
import { EcheckHelpDialogComponent } from '@egovsolutions/angular-echeck-help';
import { CardProvider, PaymentType } from '@egovsolutions/angular-payment-types';
import { RoutingNumberValidationResponse } from './routing-number-validation-response.model';

@Component({
    selector: 'egov-new-payment-method-form',
    templateUrl: './new-payment-method-form.component.html',
    styleUrls: ['./new-payment-method-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewPaymentMethodFormComponent implements OnDestroy
{
    constructor
    (
        // Dependencies

        private readonly http: HttpClient,


        // Template dependencies

        public readonly viewport: Viewport,


        // Optional dependencies

        @Optional() private readonly scrollableModal?: ScrollableModal,
        @Optional() private readonly dialogNavigations?: DialogNavigations,
    )
    {}


    // Input properties

    @Input() public set allowSelectingExisting(value: boolean) { this.allowSelectingExisting$.next(value); }
    public readonly allowSelectingExisting$ = new BehaviorSubject(false);

    @Input() public set enabled(value: boolean) { this.enabled$.next(value); }
    public readonly enabled$ = new BehaviorSubject(true);

    // When this form is used in cart-related contexts, the payment option should be passed from the cart service
    @Input() public set selectedPaymentOption(value: PaymentOption | undefined)
    { this.selectedPaymentOption$.next(value); }
    public readonly selectedPaymentOption$ = new BehaviorSubject<PaymentOption | undefined>(undefined);

    // When this form is used in cart-related contexts, the payment method should be passed from the cart service
    @Input() public selectedExistingPaymentMethod?: string;

    @Input() public set availablePaymentTypes(value: PaymentType[] | undefined)
    { this.availablePaymentTypes$.next(value); this.availablePaymentTypesUpdated(value); }
    public readonly availablePaymentTypes$ = new BehaviorSubject<PaymentType[] | undefined>(undefined);

    @Input() public continuesBelow = false;
    @Input() public blockPaymentButton = false;

    @Input() public hideContent = false;


    // Form fields

    public readonly name: FormControl = new FormControl('', [Validators.required]);

    public readonly cardNumber: FormControl = new FormControl('', [Validators.required]);
    public cardNumberValue?: string;
    public cardNumberRawValue?: string;

    public readonly expiration: FormControl =
        new FormControl('', [Validators.required, Validators.pattern(/^\d{1,2}\/\d{2}$/)]);
    public expirationValue?: string;

    private readonly cv2ValidatorsDefault = [Validators.required, Validators.pattern(/^\d{3,4}$/)];
    private readonly cv2ValidatorsAMEX = [Validators.required, Validators.pattern(/^\d{4}$/)];
    public readonly cv2: FormControl = new FormControl('', this.cv2ValidatorsDefault);
    public cv2Value?: string;

    public readonly routingNumber: FormControl = new FormControl(
        '',
        [Validators.required, Validators.pattern(/^\s*\d{9}\s*$/)],
        [this.validateRoutingNumberValidation()]
    );

    public readonly accountNumber: FormControl = new FormControl('', [Validators.required]);

    public readonly address: FormControl = new FormControl('', [Validators.required]);
    public readonly zip: FormControl = new FormControl('', [Validators.required, Validators.pattern(/^\d{5}$/)]);

    public disable()
    {
        this.enabled$.next(false);

        this.name.disable();
        this.cardNumber.disable();
        this.expiration.disable();
        this.cv2.disable();

        this.routingNumber.disable();
        this.accountNumber.disable();

        this.address.disable();
        this.zip.disable();
    }

    public enable()
    {
        this.enabled$.next(true);

        this.name.enable();
        this.cardNumber.enable();
        this.expiration.enable();
        this.cv2.enable();

        this.routingNumber.enable();
        this.accountNumber.enable();

        this.address.enable();
        this.zip.enable();
    }

    public readonly currentControls$ = this.selectedPaymentOption$.pipe(
        map(selectedPaymentOption =>
        {
            switch (selectedPaymentOption)
            {
                case PaymentType.USCard:
                    return {
                        name: this.name,
                        cardNumber: this.cardNumber,
                        expiration: this.expiration,
                        cv2: this.cv2,
                        address: this.address,
                        zip: this.zip
                    };
                case PaymentType.NonUSCard:
                    return {
                        name: this.name,
                        cardNumber: this.cardNumber,
                        expiration: this.expiration,
                        cv2: this.cv2,
                        address: this.address
                    };
                case PaymentType.ECheck:
                    return {
                        name: this.name,
                        routingNumber: this.routingNumber,
                        accountNumber: this.accountNumber,
                        address: this.address
                    };
            }

            return {};
        })
    );

    public readonly valid$ = this.currentControls$.pipe(
        switchMap(controls =>
        {
            const controlsList = [] as FormControl[];
            for (const name in controls) controlsList.push((controls as any)[name]);

            return combineLatest(controlsList.map(control =>
                control.statusChanges.pipe(map(status => status === 'VALID'))
            ));
        }),
        map(valids => valids.reduce((result, valid) => valid && result, true)),
    );

    public readonly value$ = this.currentControls$.pipe(map(controls =>
    {
        const value: any = {};

        for (const name in controls)
        {
            if (Object.prototype.hasOwnProperty.call(controls, name))
                value[name] = value[name] === this.cardNumber
                    ? this.cardNumberRawValue
                    : (controls as any)[name].value;
        }

        return value;
    }));


    // Payment option changed

    @Output() public readonly paymentOptionChanged: EventEmitter<PaymentOption> = new EventEmitter<PaymentOption>();


    // Existing payment method changed

    @Output() public readonly existingPaymentMethodSelected: EventEmitter<PaymentOption> =
        new EventEmitter<PaymentOption>();


    // Handle available payment methods change

    public availablePaymentTypesUpdated(value: PaymentType[] | undefined)
    {
        if (value?.length && !this.selectedPaymentOption$.value)
            this.selectedPaymentOption$.next(value[0]);
    }


    // Card visualization

    public readonly cardType$ = new BehaviorSubject(CardProvider.Other);
    public cv2Focused = false;

    public updateCardType(cardType: CardProvider)
    {
        switch (cardType)
        {
            case CardProvider.AMEX:
            case CardProvider.Discover:
            case CardProvider.MasterCard:
            case CardProvider.Visa:
                this.cardType$.next(cardType);
                break;

            default:
                this.cardType$.next(CardProvider.Other);
        }

        this.cv2.setValidators(cardType === CardProvider.AMEX
            ? this.cv2ValidatorsAMEX
            : this.cv2ValidatorsDefault
        );

        this.cv2.updateValueAndValidity();

    }


    // Routing number

    public readonly routingNumberStatus$: BehaviorSubject<RoutingNumberStatus> =
        new BehaviorSubject<RoutingNumberStatus>(RoutingNumberStatus.None);

    public readonly routingNumberValidationResponse$ =
        new BehaviorSubject<RoutingNumberValidationResponse | undefined>(undefined);

    private readonly routingNumberSubscription: Subscription =
        this.routingNumber.valueChanges.subscribe(value => this.validateRoutingNumber());

    private routingNumberValidationTimeout?: number;

    public validateRoutingNumber(e?: MouseEvent)
    {
        if (e) e.preventDefault();

        clearTimeout(this.routingNumberValidationTimeout);

        const routingValue = '' + this.routingNumber.value;

        if (!routingValue || routingValue.length !== 9)
        {
            this.routingNumberStatus$.next(RoutingNumberStatus.None);
            this.routingNumberValidationTimeout = window.setTimeout(() =>
            {
                this.routingNumberStatus$.next(RoutingNumberStatus.WrongLength);
            },
            2000);
        }
        else
        {
            this.routingNumberStatus$.next(RoutingNumberStatus.Validating);
            this.validateRoutingNumber$(routingValue).subscribe({
                next: response =>
                {
                    this.routingNumberValidationResponse$.next(response);
                    this.routingNumberStatus$.next(RoutingNumberStatus.Valid);
                },
                error: error =>
                {
                    if (error.message === 'invalid')
                        this.routingNumberStatus$.next(RoutingNumberStatus.Invalid);
                    else
                        this.routingNumberStatus$.next(RoutingNumberStatus.ErrorValidating);
                }
            });
        }
    }

    private validateRoutingNumber$(routingNumber: string): Observable<RoutingNumberValidationResponse>
    {
        return this.http.get(`https://routing-number-api.app.mygovonline.com/banks/${routingNumber}`)
            .pipe(map(this.adaptRoutingNumberValidation));
    }

    private adaptRoutingNumberValidation(response: any): RoutingNumberValidationResponse
    {
        if (response.message === 'OK')
            return {
                name: response.bank,
                phone: response.phone,
            };
        else throw new Error('invalid');
    }

    private validateRoutingNumberValidation(): AsyncValidatorFn
    {
        return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> =>
        {
            return this.routingNumberStatus$.pipe(
                skipWhile(value => value !== RoutingNumberStatus.Validating),
                skip(1),
                take(1),
                map(status => status === RoutingNumberStatus.Valid || status === RoutingNumberStatus.ErrorValidating
                    ? null
                    : { invalidRoutingNumber: true }
                ),
            );
        };
    }


    // Payment methods availability and selection

    public readonly allowedPaymentTypes$ = this.availablePaymentTypes$.pipe(
        map(v => v || [
            PaymentType.USCard,
            PaymentType.NonUSCard,
            PaymentType.ECheck,
        ])
    );

    public readonly tabIndex$ = combineLatest(
        [this.allowSelectingExisting$, this.selectedPaymentOption$, this.allowedPaymentTypes$]
    ).pipe(
        map(([allowSelectingExisting, selectedPaymentOption, allowedPaymentTypes]) =>
        {
            if (allowSelectingExisting)
            {
                if (selectedPaymentOption === 'EXISTING')
                    return 0;
                else
                    return 1 + allowedPaymentTypes.indexOf(selectedPaymentOption!);
            }
            else
                return allowedPaymentTypes.indexOf(selectedPaymentOption as PaymentType);
        })
    );

    public tabChanged(id: PaymentOption)
    {
        this.selectedPaymentOption$.next(id);
        this.paymentOptionChanged.emit(id);
    }

    public selectChanged(change: MatSelectChange)
    {
        this.selectedPaymentOption$.next(change.value);
        this.paymentOptionChanged.emit(change.value);
    }


    // Echeck help

    public showSmallScreensEcheckHelp()
    {
        if (this.scrollableModal)
        {
            const dialogRef = this.scrollableModal.open(EcheckHelpDialogComponent, { autoFocus: false });
            this.dialogNavigations?.set(dialogRef);
        }
    }


    // Clear subscriptions

    public ngOnDestroy()
    {
        this.routingNumberSubscription.unsubscribe();
    }
}

export enum RoutingNumberStatus
{
    /* eslint-disable @typescript-eslint/naming-convention */
    None = 'none',
    WrongLength = 'wrong-length',
    Validating = 'validating',
    ErrorValidating = 'error-validating',
    Valid = 'valid',
    Invalid = 'invalid',
    /* eslint-enable @typescript-eslint/naming-convention */
}

export type PaymentOption = PaymentType | 'EXISTING';
